/**
 * The one answer to whether a Voice Mode session still has somewhere to send.
 *
 * Two callers must agree: the lifecycle watcher (`watchSpeechLifecycle`), which ends a session
 * whose chat went away, and the background sender (`sendBackgroundVoiceTranscript`), which
 * delivers a transcript to a chat with no input on screen. A typed message needs no such check,
 * because it can only be sent from a mounted chat input, and that input exists only because the
 * router rendered its chat.
 */
import {
  isNewChatDraftId,
  resolveNewChatProjectId,
} from '@/features/chat/draft-ids';
import { littlebirdSdk } from '@/lib/sdk-init';

/**
 * Whether a spoken message from this session still has a destination: a
 * surviving New Chat draft or a chat open to interaction
 */
export function isVoiceDestinationUsable(draftId: string): boolean {
  const { drafts, projects, threads } = littlebirdSdk.stores;
  // A tab- or project-scoped New Chat session lives only as long as its draft
  if (isNewChatDraftId(draftId)) {
    const draft = drafts.getDraft(draftId);
    if (!draft) return false;
    const projectId = resolveNewChatProjectId(draftId, draft.projectId);
    return !projectId || Boolean(projects.getById(projectId));
  }
  // `isAvailableForInteraction` excludes archived and deleted chats, matching the main chat
  // lists. A bare `NEW_CHAT_ID` reaches here and finds no chat, which is intended: every
  // composer outside a tab shares that ID, so it names no single owner
  return Boolean(threads.getById(draftId)?.isAvailableForInteraction);
}
